import {
  ServiceHistoryStatus,
  VehicleAccidentState,
  VehicleUsage,
  VehicleValuationProvider,
  VehicleValuationRimType,
  VehicleValuationType,
} from "@/api-schema-enums";
import type { VehicleValuationCreateBody } from "@/common/api/vehicle-valuation/use-vehicle-valuation-create.mutation";
import type { VehicleWithRegistrationDate } from "@/common/api/vehicle/vehicle.types";
import {
  DateInputValidationError,
  dateToInputString,
  getDateInputValidationMessage,
  inputStringToDate,
  parseApiDateOnly,
  validateDateInputString,
} from "@/common/helpers/date-input";
import { formatDateToApi, formatDateToLocale } from "@/common/helpers/format-date";
import { isLocalizedNumberInput, parseLocalizedNumberInput } from "@/common/helpers/number-format";
import { normalizeVehicleVin, VIN_REGEX } from "@/common/helpers/vehicle-vin";
import {
  addYearsToLocalDate,
  createTodayDate,
  getVehicleValuationTargetDateBounds,
  isVehicleValuationTargetDateAllowed,
  toLocalIsoDate,
} from "@/components/vehicle-valuation/valuation-date";
import { z } from "zod";
import {
  isVehicleAccidentState,
  isVehicleBooleanSelectValue,
  isVehicleValuationRimType,
  parseVehicleBooleanSelectValue,
  shouldShowVehicleAccidentDetails,
  toVehicleBooleanSelectValue,
  VEHICLE_ACCIDENT_STATE_VALUES,
  VEHICLE_BOOLEAN_SELECT_VALUES,
  VEHICLE_VALUATION_CREATE_TYPE_VALUES,
  VEHICLE_VALUATION_RIM_TYPE_VALUES,
} from "../../_helpers/vehicle-valuation-metadata";

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;
type CreateFormFieldPath =
  | "earlyLeaseTerminationAmount"
  | "accidentDescription"
  | "accidentValue"
  | "exchangeDate"
  | "vehicleMileageAtExchange";

const VEHICLE_USAGE_VALUES = [
  VehicleUsage.RegularPersonalCommercial,
  VehicleUsage.RENTAL,
  VehicleUsage.TaxiRideHailing,
  VehicleUsage.DrivingSchool,
  VehicleUsage.DEMO,
  VehicleUsage.FleetPool,
  VehicleUsage.DeliveryCourier,
  VehicleUsage.SpecialPurpose,
  VehicleUsage.CONSTRUCTION,
  VehicleUsage.ManagementCompanyCar,
  VehicleUsage.EmployeeCompanyCar,
  VehicleUsage.UNKNOWN,
] as const;

const SERVICE_HISTORY_VALUES = [
  ServiceHistoryStatus.UNKNOWN,
  ServiceHistoryStatus.NONE,
  ServiceHistoryStatus.PARTIAL,
  ServiceHistoryStatus.FullServiceHistory,
  ServiceHistoryStatus.FullDealerServiceHistory,
] as const;

export const MAX_VEHICLE_AGE_YEARS = 20;

export function getMinRegistrationDate(now = new Date()): Date {
  return addYearsToLocalDate(createTodayDate(now), -MAX_VEHICLE_AGE_YEARS);
}

function isNonNegativeIntegerInput(value: string): boolean {
  return isLocalizedNumberInput(value, { allowNegative: false, integerOnly: true });
}

function isNonNegativeDecimalInput(value: string | undefined): boolean {
  return isLocalizedNumberInput(value ?? "", { allowNegative: false });
}

function parseNonNegativeIntegerInput(value: string): number {
  return parseLocalizedNumberInput(value, { allowNegative: false, integerOnly: true });
}

function parseNonNegativeDecimalInput(value: string | undefined): number {
  return parseLocalizedNumberInput(value ?? "", { allowNegative: false });
}

function getVehicleValuationTargetDateValidationMessage(now: Date): string {
  const { firstFutureDate, maxDate } = getVehicleValuationTargetDateBounds(now);

  return JSON.stringify({
    key: "fab:VehicleValuationTargetDateRangeError",
    options: {
      startDate: formatDateToLocale(toLocalIsoDate(firstFutureDate), { dateOnly: true }),
      endDate: formatDateToLocale(toLocalIsoDate(maxDate), { dateOnly: true }),
    },
  });
}

export function getVehicleValuationCreateFormSchema(
  t: TranslateFn,
  requireVin: boolean,
  requireRegistrationDate: boolean,
  now = new Date(),
) {
  return z
    .object({
      vin: requireVin
        ? z
            .string()
            .trim()
            .min(1, t("masterdata:VinIsRequired"))
            .length(17, t("masterdata:VehicleVinMustBe17Characters"))
            .regex(VIN_REGEX, t("masterdata:VehicleVinInvalidFormat"))
        : z.string().trim().optional(),
      valuationDate: z
        .string()
        .trim()
        .min(1, t("fab:VehicleValuationTargetDateIsRequired"))
        .superRefine((value, context) => {
          const parsedValue = inputStringToDate(value);

          if (!parsedValue) {
            context.addIssue({
              code: "custom",
              message: getDateInputValidationMessage(DateInputValidationError.InvalidFormat),
            });
            return;
          }

          if (!isVehicleValuationTargetDateAllowed(parsedValue, now)) {
            context.addIssue({
              code: "custom",
              message: getVehicleValuationTargetDateValidationMessage(now),
            });
          }
        })
        .transform((value) => inputStringToDate(value) as Date),
      registrationDate: z
        .string()
        .trim()
        .superRefine((value, context) => {
          if (requireRegistrationDate && !value) {
            context.addIssue({
              code: "custom",
              message: t("fab:RegistrationDateIsRequired"),
            });
            return;
          }

          const error = validateDateInputString(value, {
            disallowFutureDate: true,
            minDate: getMinRegistrationDate(now),
          });

          if (error === DateInputValidationError.BeforeMinDate) {
            context.addIssue({ code: "custom", message: t("fab:VehicleValuationMaxVehicleAgeError") });
            return;
          }

          if (error) {
            context.addIssue({ code: "custom", message: getDateInputValidationMessage(error) });
          }
        })
        .transform((value) => inputStringToDate(value)),
      huDate: z
        .string()
        .trim()
        .superRefine((value, context) => {
          const error = validateDateInputString(value, { disallowFutureDate: true });

          if (error) {
            context.addIssue({ code: "custom", message: getDateInputValidationMessage(error) });
          }
        })
        .transform((value) => inputStringToDate(value)),
      mileage: z
        .string()
        .trim()
        .min(1, t("fab:MileageIsRequired"))
        .refine(isNonNegativeIntegerInput, t("fab:MileageMustBeANumber")),
      numberOfHolders: z
        .string()
        .trim()
        .min(1, t("fab:NumberOfHoldersIsRequired"))
        .refine(isNonNegativeIntegerInput, t("fab:NumberOfHoldersMustBeANumber"))
        .refine(
          (value) => !isNonNegativeIntegerInput(value) || parseNonNegativeIntegerInput(value) <= 15,
          t("fab:NumberOfHoldersMustBeAtMost15"),
        ),
      accidentState: z
        .union([z.literal(""), z.enum(VEHICLE_ACCIDENT_STATE_VALUES)])
        .refine((value): value is VehicleAccidentState => isVehicleAccidentState(value), {
          message: t("masterdata:AccidentStateIsRequired"),
        }),
      rimType: z
        .union([z.literal(""), z.enum(VEHICLE_VALUATION_RIM_TYPE_VALUES)])
        .refine((value): value is VehicleValuationRimType => isVehicleValuationRimType(value), {
          message: t("fab:RimTypeIsRequired"),
        }),
      valuationType: z.enum(VEHICLE_VALUATION_CREATE_TYPE_VALUES).default(VehicleValuationType.Default),
      earlyLeaseTermination: z.boolean().default(false),
      earlyLeaseTerminationAmount: z.string().trim().optional(),
      accidentDescription: z.string().trim().optional(),
      accidentValue: z.string().trim().optional(),
      note: z.string().trim().optional(),
      vehicleUsage: z
        .union([z.literal(""), z.enum(VEHICLE_USAGE_VALUES)])
        .refine((value): value is VehicleUsage => value !== "", {
          message: t("masterdata:VehicleUsageIsRequired"),
        }),
      serviceHistory: z.union([z.literal(""), z.enum(SERVICE_HISTORY_VALUES)]).optional(),
      exchangeEngineInstalled: z.boolean().default(false),
      exchangeDate: z.string().trim(),
      vehicleMileageAtExchange: z.string().trim(),
      engineMileageAtExchange: z.string().trim(),
      exchangeNotes: z.string().trim(),
      vatDeductible: z
        .union([z.literal(""), z.enum(VEHICLE_BOOLEAN_SELECT_VALUES)])
        .refine(isVehicleBooleanSelectValue, {
          message: t("masterdata:VatDeductibleIsRequired"),
        }),
    })
    .superRefine((values, context) => {
      const validateRequiredField = ({
        value,
        path,
        message,
      }: {
        value?: string;
        path: CreateFormFieldPath;
        message: string;
      }): boolean => {
        if (value) {
          return true;
        }

        context.addIssue({
          code: "custom",
          message,
          path: [path],
        });

        return false;
      };

      if (values.earlyLeaseTermination) {
        const hasEarlyLeaseTerminationAmount = validateRequiredField({
          value: values.earlyLeaseTerminationAmount,
          path: "earlyLeaseTerminationAmount",
          message: t("fab:EarlyLeaseTerminationAmountIsRequired"),
        });

        if (hasEarlyLeaseTerminationAmount && !isNonNegativeDecimalInput(values.earlyLeaseTerminationAmount)) {
          context.addIssue({
            code: "custom",
            message: t("fab:EarlyLeaseTerminationAmountMustBeANumber"),
            path: ["earlyLeaseTerminationAmount"],
          });
        }
      }

      if (values.exchangeEngineInstalled) {
        const hasExchangeDate = validateRequiredField({
          value: values.exchangeDate,
          path: "exchangeDate",
          message: t("masterdata:ExchangeDateIsRequired"),
        });

        if (hasExchangeDate) {
          const exchangeDateError = validateDateInputString(values.exchangeDate, { disallowFutureDate: true });

          if (exchangeDateError) {
            context.addIssue({
              code: "custom",
              message: getDateInputValidationMessage(exchangeDateError),
              path: ["exchangeDate"],
            });
          }
        }

        const hasVehicleMileageAtExchange = validateRequiredField({
          value: values.vehicleMileageAtExchange,
          path: "vehicleMileageAtExchange",
          message: t("masterdata:VehicleMileageAtExchangeIsRequired"),
        });

        if (hasVehicleMileageAtExchange && !isNonNegativeIntegerInput(values.vehicleMileageAtExchange)) {
          context.addIssue({
            code: "custom",
            message: t("masterdata:VehicleMileageAtExchangeIsRequired"),
            path: ["vehicleMileageAtExchange"],
          });
        }

        if (values.engineMileageAtExchange && !isNonNegativeIntegerInput(values.engineMileageAtExchange)) {
          context.addIssue({
            code: "custom",
            message: t("masterdata:EngineMileageAtExchangeMustBeANumber"),
            path: ["engineMileageAtExchange"],
          });
        }
      }

      const requiresAccidentDetails =
        isVehicleAccidentState(values.accidentState) && shouldShowVehicleAccidentDetails(values.accidentState);

      if (!requiresAccidentDetails) {
        return;
      }

      validateRequiredField({
        value: values.accidentDescription,
        path: "accidentDescription",
        message: t("fab:AccidentDescriptionIsRequired"),
      });

      const hasAccidentValue = validateRequiredField({
        value: values.accidentValue,
        path: "accidentValue",
        message: t("fab:AccidentValueIsRequired"),
      });

      if (hasAccidentValue && !isNonNegativeDecimalInput(values.accidentValue)) {
        context.addIssue({
          code: "custom",
          message: t("fab:AccidentValueMustBeANumber"),
          path: ["accidentValue"],
        });
      }
    });
}

export type VehicleValuationCreateFormSchema = ReturnType<typeof getVehicleValuationCreateFormSchema>;
export type VehicleValuationCreateFormInputValues = z.input<VehicleValuationCreateFormSchema>;
export type VehicleValuationCreateFormValues = z.output<VehicleValuationCreateFormSchema>;

export function getVehicleValuationCreateFormDefaultValues({
  defaultVin,
  vehicle,
  includeRegistrationDate = true,
}: {
  defaultVin?: string;
  vehicle?: VehicleWithRegistrationDate | null;
  includeRegistrationDate?: boolean;
}): VehicleValuationCreateFormInputValues {
  return {
    vin: defaultVin ?? vehicle?.vin ?? "",
    valuationDate: dateToInputString(createTodayDate()),
    registrationDate: includeRegistrationDate ? dateToInputString(parseApiDateOnly(vehicle?.registrationDate)) : "",
    huDate: dateToInputString(parseApiDateOnly(vehicle?.huDate)),
    mileage: vehicle?.mileage?.toString() ?? "",
    numberOfHolders: vehicle?.numberOfHolders?.toString() ?? "",
    accidentState: vehicle?.accidentState ?? "",
    rimType: vehicle?.rimType ?? "",
    valuationType: VehicleValuationType.Default,
    earlyLeaseTermination: false,
    earlyLeaseTerminationAmount: "",
    accidentDescription: "",
    accidentValue: "",
    note: "",
    vehicleUsage: vehicle?.vehicleUsage ?? "",
    serviceHistory: vehicle?.serviceHistory ?? "",
    exchangeEngineInstalled: !!vehicle?.exchangeEngine,
    exchangeDate: dateToInputString(parseApiDateOnly(vehicle?.exchangeEngine?.exchangeDate)),
    vehicleMileageAtExchange: vehicle?.exchangeEngine?.vehicleMileageAtExchange?.toString() ?? "",
    engineMileageAtExchange: vehicle?.exchangeEngine?.engineMileageAtExchange?.toString() ?? "",
    exchangeNotes: vehicle?.exchangeEngine?.notes ?? "",
    vatDeductible: toVehicleBooleanSelectValue(vehicle?.vatDeductible),
  };
}

export function getVehicleValuationCreateFormInitializationKey({
  defaultVin,
  vehicle,
}: {
  defaultVin?: string;
  vehicle?: VehicleWithRegistrationDate | null;
}): string {
  return JSON.stringify({
    defaultVin: defaultVin ?? "",
    vehicleUuid: vehicle?.uuid ?? "",
    vin: vehicle?.vin ?? "",
    registrationDate: vehicle?.registrationDate ?? "",
    huDate: vehicle?.huDate ?? "",
    mileage: vehicle?.mileage ?? null,
    numberOfHolders: vehicle?.numberOfHolders ?? null,
    accidentState: vehicle?.accidentState ?? "",
    rimType: vehicle?.rimType ?? "",
    vehicleUsage: vehicle?.vehicleUsage ?? "",
    serviceHistory: vehicle?.serviceHistory ?? "",
    exchangeEngine: vehicle?.exchangeEngine
      ? {
          exchangeDate: vehicle.exchangeEngine.exchangeDate,
          vehicleMileageAtExchange: vehicle.exchangeEngine.vehicleMileageAtExchange,
          engineMileageAtExchange: vehicle.exchangeEngine.engineMileageAtExchange ?? null,
          notes: vehicle.exchangeEngine.notes ?? null,
        }
      : null,
    vatDeductible: vehicle?.vatDeductible ?? null,
  });
}

export interface BuildVehicleValuationCreateBodyOptions {
  requester?: string | null;
  vehicleUuid?: string;
  vin?: string;
}

export function buildVehicleValuationCreateBody(
  values: VehicleValuationCreateFormValues,
  { requester, vehicleUuid, vin }: BuildVehicleValuationCreateBodyOptions,
): VehicleValuationCreateBody {
  if (!requester) {
    throw new Error("Authenticated user UUID is required to create a vehicle valuation");
  }

  if (
    !isVehicleAccidentState(values.accidentState) ||
    !isVehicleValuationRimType(values.rimType) ||
    !isVehicleBooleanSelectValue(values.vatDeductible)
  ) {
    throw new Error("Required vehicle valuation metadata is missing");
  }

  const normalizedVin = vehicleUuid ? undefined : normalizeVehicleVin(vin ?? values.vin ?? "");

  if (!vehicleUuid && !normalizedVin) {
    throw new Error("VIN is required to create a vehicle valuation without an existing vehicle");
  }

  const requiresAccidentDetails = shouldShowVehicleAccidentDetails(values.accidentState);

  return {
    ...(vehicleUuid ? { vehicle: vehicleUuid } : { vin: normalizedVin }),
    requester,
    provider: VehicleValuationProvider.Dat,
    valuationDate: formatDateToApi(values.valuationDate),
    registrationDate: values.registrationDate ? formatDateToApi(values.registrationDate) : undefined,
    huDate: values.huDate ? formatDateToApi(values.huDate) : null,
    mileage: parseNonNegativeIntegerInput(values.mileage),
    numberOfHolders: parseNonNegativeIntegerInput(values.numberOfHolders),
    accidentState: values.accidentState,
    rimType: values.rimType,
    valuationType: values.valuationType,
    earlyLeaseTermination: values.earlyLeaseTermination,
    ...(values.earlyLeaseTermination
      ? { earlyLeaseTerminationAmount: parseNonNegativeDecimalInput(values.earlyLeaseTerminationAmount) }
      : {}),
    ...(requiresAccidentDetails
      ? {
          accidentDescription: values.accidentDescription?.trim(),
          accidentValue: parseNonNegativeDecimalInput(values.accidentValue),
        }
      : {}),
    ...(values.note?.trim() ? { note: values.note.trim() } : {}),
    vehicleUsage: values.vehicleUsage,
    serviceHistory: values.serviceHistory || null,
    ...(values.exchangeEngineInstalled
      ? {
          exchangeEngine: {
            exchangeDate: formatDateToApi(inputStringToDate(values.exchangeDate)!),
            vehicleMileageAtExchange: parseNonNegativeIntegerInput(values.vehicleMileageAtExchange),
            engineMileageAtExchange: values.engineMileageAtExchange
              ? parseNonNegativeIntegerInput(values.engineMileageAtExchange)
              : null,
            notes: values.exchangeNotes.trim() || null,
          },
        }
      : {}),
    vatDeductible: parseVehicleBooleanSelectValue(values.vatDeductible),
  };
}
